import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import type { ResourceSummary } from '../../resources/model/types'
import { actionView } from '../../../shared/views'
import type { MutationResultLike } from '../../../shared/views'
import type {
  CatalogLinkResult,
  CatalogLinksIntentState,
  CatalogLinksVm,
  LinkedProduct,
  LinkedProductVm,
  ResourceChoice,
} from './types'

export interface CatalogLinksVmInput {
  intent: CatalogLinksIntentState
  resources: QueryResultLike<readonly ResourceSummary[]>
  /** Связанные товары выбранного ресурса; `null` — ресурс списка не выбран. */
  list: QueryResultLike<readonly LinkedProduct[]> | null
  link: MutationResultLike<CatalogLinkResult>
  unlink: MutationResultLike<CatalogLinkResult>
  /** Название вида ресурса — от пакета вида (реестр форм), а не ветвлением здесь. */
  kindTitle: (kind: string) => string
}

const NO_CHOICES: readonly ResourceChoice[] = []
const NO_PRODUCTS: readonly LinkedProductVm[] = []
const IDLE: QueryView = { status: 'idle', refreshing: false, error: null }

/** Откуда взялась связь — словами (общий список для «Связей товаров» и «Общего каталога»). */
export const ORIGIN_LABELS: Readonly<Record<string, string>> = {
  Manual: 'связано вручную',
  Sync: 'создано синхронизацией',
  Matched: 'сопоставлено по штрихкоду или артикулу',
}

function viewOf<T>(result: QueryResultLike<T>, isEmpty: (data: T) => boolean): QueryView {
  return queryView(
    {
      isUninitialized: result.isUninitialized,
      isLoading: result.isLoading,
      isError: result.isError,
      isSuccess: result.isSuccess,
      ...(result.error !== undefined ? { error: result.error } : {}),
      ...(result.data !== undefined ? { data: result.data } : {}),
    },
    isEmpty,
  )
}

/** Почему связать нельзя — первая недостающая часть словами; `null` — можно. */
export function linkBlockerOf(intent: CatalogLinksIntentState): string | null {
  const draft = intent.draft
  if (draft.firstResourceId === '' || draft.secondResourceId === '') return 'Выберите ресурсы обеих карточек'
  if (draft.firstResourceId.toLowerCase() === draft.secondResourceId.toLowerCase())
    return 'У товара одна карточка на ресурс: выберите два разных ресурса'
  if (draft.firstKey.trim() === '' || draft.secondKey.trim() === '') return 'Укажите ключи обеих карточек'
  if (draft.reason.trim() === '') return 'Укажите причину — она попадает в журнал связей'
  return null
}

function outcomeOf(result: CatalogLinkResult | undefined): string | null {
  if (result === undefined) return null
  const name = result.product === null ? 'товар' : `товар «${result.product.name}»`
  if (result.replayed) return `Операция уже была выполнена — повтор ничего не изменил: ${name}`
  if (result.productCreated) return `Связано: заведён общий ${name}, карточек — ${result.changed.length}`
  if (result.changed.length === 0) return `Карточки уже связаны: ${name}`
  return `Связано: к ${result.product === null ? 'товару' : `товару «${result.product.name}»`} присоединено карточек — ${result.changed.length}`
}

/** Фабрика: один экземпляр на смонтированный binding (D07). */
export const makeSelectCatalogLinksVm = () => {
  const selectResources = createSelector(
    [(input: CatalogLinksVmInput) => input.resources],
    (resources) => viewOf(resources, (data) => data.length === 0),
  )
  const selectChoices = createSelector(
    [(input: CatalogLinksVmInput) => input.resources.data, (input: CatalogLinksVmInput) => input.kindTitle],
    (resources, kindTitle): readonly ResourceChoice[] =>
      resources === undefined
        ? NO_CHOICES
        : resources
            .filter((resource) => resource.enabled)
            .map((resource) => ({ value: resource.id, label: resource.displayName, mark: kindTitle(resource.kind) })),
  )
  const selectList = createSelector([(input: CatalogLinksVmInput) => input.list], (list) =>
    list === null ? IDLE : viewOf(list, (data) => data.length === 0),
  )
  const selectProducts = createSelector(
    [(input: CatalogLinksVmInput) => input.list?.data, (input: CatalogLinksVmInput) => input.resources.data, (input: CatalogLinksVmInput) => input.kindTitle],
    (products, resources, kindTitle): readonly LinkedProductVm[] => {
      if (products === undefined || products.length === 0) return NO_PRODUCTS
      const index = new Map((resources ?? []).map((resource) => [resource.id.toLowerCase(), resource]))
      return products.map((product) => ({
        id: product.id,
        name: product.name,
        identities: product.identities.map((identity) => {
          const resource = index.get(identity.resourceId.toLowerCase())
          return {
            resourceId: identity.resourceId,
            resourceLabel: resource === undefined ? `ресурс ${identity.resourceId}` : `${resource.displayName} (${kindTitle(resource.kind)})`,
            remoteKey: identity.remoteKey,
            originLabel: ORIGIN_LABELS[identity.origin] ?? identity.origin,
          }
        }),
        hiddenNote:
          product.hiddenIdentities === 0 ? null : `Ещё связей с ресурсами, к которым у вас нет допуска: ${product.hiddenIdentities}`,
      }))
    },
  )

  return createSelector(
    [
      selectResources,
      selectChoices,
      (input: CatalogLinksVmInput) => input.intent,
      (input: CatalogLinksVmInput) => input.link,
      (input: CatalogLinksVmInput) => input.unlink,
      selectList,
      selectProducts,
    ],
    (resources, choices, intent, link, unlink, list, products): CatalogLinksVm => {
      const linkAction = actionView(link)
      const blocker = linkBlockerOf(intent)
      return {
        resources,
        choices,
        draft: intent.draft,
        canLink: resources.status === 'ready' && blocker === null && !linkAction.busy,
        linkBlocker: blocker,
        linkAction,
        linkOutcome: link.isSuccess ? outcomeOf(link.data) : null,
        listResourceId: intent.listResourceId,
        list,
        products,
        unlinkAction: actionView(unlink),
      }
    },
  )
}
